/**
 * Paleta de colores centralizada para todos los widgets de gráficas del dashboard.
 * Usar DEFAULT_CHART_COLORS para la mayoría de los casos.
 * Reemplaza los arrays hardcodeados dispersos en ChartLine, ChartVentasMensual,
 * ChartBarHorizontal, ChartDonut y FieldMappingPanel.
 */

export const CHART_COLORS = {
  /** Azules — paleta principal para series y barras */
  primary:   ['#3182CE', '#90CDF4', '#2B6CB0', '#63B3ED', '#4299E1'],
  /** Líneas — mayor contraste para series temporales */
  lines:     ['#0066FF', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'],
  /** Verde esmeralda — para indicadores positivos */
  emerald:   ['#38A169', '#9AE6B4', '#276749', '#68D391', '#48BB78'],
  /** Cálidos — para distribuciones y donuts */
  warm:      ['#DD6B20', '#FBD38D', '#C05621', '#F6AD55', '#ED8936'],
};

/** Paleta por defecto para barras (horizontales y verticales) */
export const DEFAULT_CHART_COLORS = CHART_COLORS.primary;

/** Paleta para gráficas de líneas (mayor contraste entre series) */
export const LINE_CHART_COLORS = CHART_COLORS.lines;

/** Colores para chips de campos en FieldMappingPanel */
export const FIELD_MAPPING_COLORS = CHART_COLORS.primary;

/* ------------------------------------------------------------------ *
 * Paleta categórica del tablero de indicadores
 * ------------------------------------------------------------------ */

/**
 * Paleta para series y categorías del tablero.
 *
 * <b>Sustituye a `DEFAULT_CHART_COLORS` en los widgets del dashboard</b>, que son
 * cinco tonos del mismo azul: con dos categorías ya cuesta distinguirlas, y con
 * ocho es ilegible. Aquí cada entrada es un hue distinto.
 *
 * <b>Rojo y verde no están, y no es un olvido.</b> Los dos portan significado en
 * el tablero — un delta favorable es verde y uno desfavorable es rojo — así que
 * usarlos para "Alimentos" o "Bodega Norte" haría que el color dejara de
 * significar algo. El verde oliva del índice 6 está lo bastante lejos del verde
 * de éxito como para no confundirse con él.
 *
 * El gris del índice 7 está <b>reservado para "Otros"</b>: el grupo que absorbe
 * la cola cuando hay más categorías que colores.
 */
export const CATEGORICAL_PALETTE = [
  '#378ADD', // 1 azul
  '#1D9E75', // 2 teal
  '#D85A30', // 3 coral
  '#BA7517', // 4 ámbar
  '#7F77DD', // 5 púrpura
  '#D4537E', // 6 rosa
  '#639922', // 7 verde oliva
  '#888780', // 8 gris — reservado para "Otros"
] as const;

/** El color con el que se pinta el grupo que absorbe la cola de categorías. */
export const OTHERS_COLOR = CATEGORICAL_PALETTE[CATEGORICAL_PALETTE.length - 1];

/** Clave con la que el tablero agrupa la cola. Se traduce al pintar, no aquí. */
export const OTHERS_KEY = '__others__';

/** Cuántas categorías reales existen antes de que el resto caiga en "Otros". */
export const MAX_CATEGORIES = CATEGORICAL_PALETTE.length - 1;

/**
 * Hash estable de una cadena. FNV-1a: barato, bien distribuido y —lo que importa
 * aquí— **determinista entre sesiones y entre máquinas**.
 */
function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/**
 * El color de una categoría, estable por su nombre.
 *
 * <b>Por qué por nombre y no por índice del array de datos.</b> Si el color
 * saliera de la posición, "Alimentos" cambiaría de color cada vez que cambia el
 * orden — y el orden cambia al cambiar de periodo, al cambiar de compañía y al
 * pasar de dona a barras, porque las tres cosas reordenan por valor. El usuario
 * vería la misma categoría de dos colores distintos en la misma pantalla.
 *
 * Con el hash, "Alimentos" es del mismo color siempre y en todas partes.
 *
 * El gris de "Otros" queda fuera de la rotación: se reserva para el grupo de cola,
 * y ninguna categoría real puede recibirlo por sorteo.
 */
export function colorForCategory(category: string): string {
  if (category === OTHERS_KEY) return OTHERS_COLOR;
  return CATEGORICAL_PALETTE[hashString(category) % MAX_CATEGORIES];
}

/**
 * Los colores de un gráfico COMPLETO: estables por nombre y <b>sin repetirse entre sí</b>.
 *
 * <b>Por qué no basta con `colorForCategory` fila a fila.</b> `hash % 7` es un sorteo sin
 * desempate: con siete categorías, la probabilidad de que las siete caigan en colores distintos
 * es del <b>0,61 %</b>. Medido sobre nombres reales del dominio, siete categorías de producto
 * producían cuatro colores y «Alimentos» compartía el suyo con «Bebidas» — que es exactamente el
 * defecto que esta paleta vino a corregir, y en una dona dos segmentos contiguos del mismo color
 * se leen como uno solo.
 *
 * El hash sigue mandando: da el color <b>preferido</b> de cada categoría, así que «Alimentos»
 * tiende siempre al mismo. Lo que se añade es un sondeo lineal para el empate, y un orden de
 * recorrido alfabético —independiente del orden en que lleguen los datos, que cambia al cambiar
 * de periodo, de compañía o de tipo de gráfico— para que el mismo conjunto de categorías
 * produzca siempre la misma asignación.
 *
 * @example
 * const colores = colorsForCategories(rows.map(r => String(r[categoryField])));
 * <Cell fill={colores[nombre]} />
 */
export function colorsForCategories(categories: string[]): Record<string, string> {
  const asignados = new Map<number, string>();
  const resultado: Record<string, string> = {};

  for (const category of [...new Set(categories)].sort()) {
    if (category === OTHERS_KEY) {
      resultado[category] = OTHERS_COLOR;
      continue;
    }
    let i = hashString(category) % MAX_CATEGORIES;
    // Sondeo lineal: si el preferido ya está tomado en ESTE gráfico, el siguiente libre.
    while (asignados.has(i) && asignados.size < MAX_CATEGORIES) i = (i + 1) % MAX_CATEGORIES;
    asignados.set(i, category);
    resultado[category] = CATEGORICAL_PALETTE[i];
  }

  return resultado;
}

/**
 * El color de la serie número {@link index} de un gráfico multi-serie.
 *
 * Recorre solo los {@link MAX_CATEGORIES} colores reales: `index % CATEGORICAL_PALETTE.length`
 * llegaba al gris del último índice, que está reservado para «Otros».
 */
export function colorForSeries(index: number): string {
  return CATEGORICAL_PALETTE[index % MAX_CATEGORIES];
}
